import { useEffect, useState, useCallback } from "react";
import { Plus, PencilSimple, Trash, MapPin } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import Modal, { Field, inputCls, PrimaryBtn, GhostBtn } from "@/components/Modal";
import { toast } from "sonner";

const COLORS = ["#2C3E35", "#B86B5A", "#7A8B5C", "#C9A227", "#5A7A8B", "#8B5A7A"];
const empty = { name: "", title: "Stylist", email: "", phone: "", color: "#2C3E35", service_ids: [], location_id: "", active: true };

export default function Staff() {
  const { t } = useI18n();
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/staff");
    setStaff(data);
  }, []);
  useEffect(() => { load(); api.get("/locations").then(({ data }) => setLocations(data)); }, [load]);

  const openNew = () => { setForm(empty); setDialog({ open: true, id: null }); };
  const openEdit = (s) => { setForm({ name: s.name, title: s.title || "Stylist", email: s.email || "", phone: s.phone || "", color: s.color || "#2C3E35", service_ids: s.service_ids || [], location_id: s.location_id || "", active: s.active !== false }); setDialog({ open: true, id: s.id }); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) { toast.error(t.cust.nameReq); return; }
    setSaving(true);
    try {
      const payload = { ...form, location_id: form.location_id || null };
      if (dialog.id) await api.put(`/staff/${dialog.id}`, payload);
      else await api.post("/staff", payload);
      toast.success(dialog.id ? t.staff.updated : t.staff.added);
      setDialog({ open: false, id: null });
      load();
    } catch (e) { toast.error(t.dlg.failed); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/staff/${id}`); toast.success(t.staff.removed); load(); };

  const locMap = Object.fromEntries(locations.map((l) => [l.id, l.name]));

  return (
    <div className="fade-up space-y-6" data-testid="staff-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.staff.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.staff.title}</h1>
        </div>
        <button data-testid="new-staff-btn" onClick={openNew}
          className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
          <Plus size={16} weight="bold" /> {t.staff.add}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className="bg-white rounded-xl border border-[#E5E2DC] p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm group">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full text-white flex items-center justify-center text-sm font-bold" style={{ background: s.color }}>
                  {s.name.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-sm">{s.name}</div>
                  <div className="text-xs text-[#6B6A67]">{s.title}</div>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`edit-staff-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF]"><PencilSimple size={15} /></button>
                <button data-testid={`del-staff-${s.id}`} onClick={() => del(s.id)} className="p-1.5 rounded-lg text-[#6B6A67] hover:bg-[#B86B5A]/12 hover:text-[#B86B5A]"><Trash size={15} /></button>
              </div>
            </div>
            <div className="mt-3 text-xs text-[#6B6A67] space-y-1">
              {s.email && <div>{s.email}</div>}
              {s.location_id && locMap[s.location_id] && <div className="inline-flex items-center gap-1 text-[#5d6b46]"><MapPin size={12} weight="fill" /> {locMap[s.location_id]}</div>}
              <div><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.active !== false ? "bg-[#7A8B5C]/15 text-[#5d6b46]" : "bg-[#E8E4DF] text-[#6B6A67]"}`}>{s.active !== false ? t.staff.active : t.staff.inactive}</span></div>
            </div>
          </div>
        ))}
        {staff.length === 0 && <div className="col-span-full py-16 text-center text-[#6B6A67] text-sm">{t.staff.title}</div>}
      </div>

      <Modal open={dialog.open} onClose={() => setDialog({ open: false, id: null })} testid="staff-dialog"
        title={dialog.id ? t.staff.edit : t.staff.addTitle}
        footer={<><GhostBtn onClick={() => setDialog({ open: false, id: null })}>{t.btn.cancel}</GhostBtn><PrimaryBtn onClick={save} disabled={saving} data-testid="staff-save">{saving ? t.btn.saving : t.btn.save}</PrimaryBtn></>}>
        <div className="space-y-4">
          <Field label={t.staff.fName}><input data-testid="staff-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Camille Rivera" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.staff.fTitle}><input data-testid="staff-title" className={inputCls} value={form.title} onChange={set("title")} /></Field>
            <Field label={t.staff.fEmail}><input data-testid="staff-email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
          </div>
          <Field label={t.staff.fColor}>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                  className={`h-8 w-8 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-[#1A1A1A] scale-110" : ""}`} style={{ background: c }} />
              ))}
            </div>
          </Field>
          {locations.length > 0 && (
            <Field label={t.loc.assign}>
              <select data-testid="staff-location" className={inputCls} value={form.location_id || ""} onChange={set("location_id")}>
                <option value="">{t.loc.noLoc}</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
          )}
        </div>
      </Modal>
    </div>
  );
}
